import { useState, useEffect, useCallback } from 'react';
import {
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    signOut as firebaseSignOut,
    onAuthStateChanged,
} from 'firebase/auth';
import { auth, googleProvider } from './firebase';

export const useFirebaseAuth = (options = {}) => {
    const {
        context = 'user',
        onAuthChange,
        allowedDomains = null,
        useRedirect = false
    } = options;

    const [user, setUser] = useState(null); // This will now store the full Firebase User object
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [initialLoading, setInitialLoading] = useState(true);

    // Helper to validate domain and update state
    const validateAndSetUser = useCallback(async (firebaseUser) => {
        if (!firebaseUser) {
            setUser(null);
            onAuthChange?.(null);
            setError(''); // Clear error if no user
            return false;
        }

        // Domain restriction check
        if (allowedDomains && firebaseUser.email) {
            if (!allowedDomains.some(domain => firebaseUser.email.endsWith(`@${domain}`))) {
                setError(`Access restricted. Please use an email from: ${allowedDomains.join(', ')}`);
                await firebaseSignOut(auth); // Sign out unauthorized user
                setUser(null); // Ensure user is nullified if unauthorized
                onAuthChange?.(null);
                return false; // User not authorized
            }
        }

        // If domain is fine, set the full Firebase user object
        // The `useSecureAdminAuth` hook will then be able to call `firebaseUser.getIdToken()`
        setUser(firebaseUser);
        setError(''); // Clear any previous errors

        // You might still want to pass some basic info to onAuthChange
        onAuthChange?.({
            uid: firebaseUser.uid,
            displayName: firebaseUser.displayName,
            email: firebaseUser.email,
            photoURL: firebaseUser.photoURL,
            emailVerified: firebaseUser.emailVerified,
            context,
            // You can also pass the full firebaseUser if onAuthChange needs it
            // firebaseUser: firebaseUser
        });

        return true; // User authorized and set
    }, [allowedDomains, context, onAuthChange]);


    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            // This listener fires when auth state changes (login, logout, token refresh)
            setLoading(true); // Indicate loading while validation happens
            await validateAndSetUser(firebaseUser);
            setLoading(false); // End loading after validation
            setInitialLoading(false);
        });

        // Handle redirect results on initial load (only if useRedirect is true)
        if (useRedirect) {
            getRedirectResult(auth)
                .then(async (result) => {
                    if (result) {
                        console.log('useFirebaseAuth: Redirect sign-in successful:', result.user.email);
                        // The onAuthStateChanged listener will eventually fire and handle
                        // setting the user. No need to call validateAndSetUser here directly for `result.user`.
                    }
                    // For redirect flow, it's crucial to ensure initialLoading is set to false
                    // even if no result, to prevent UI from hanging.
                    setInitialLoading(false);
                })
                .catch((err) => {
                    console.error('useFirebaseAuth: Google sign-in redirect error:', err);
                    let errorMessage = 'Failed to sign in with Google. Please try again.';

                    if (err.code === 'auth/cancelled-popup-request' || err.code === 'auth/popup-closed-by-user') {
                        errorMessage = 'Sign-in was cancelled. Please try again.';
                    } else if (err.code === 'auth/popup-blocked') {
                        errorMessage = 'Pop-up was blocked by your browser. Please allow pop-ups and try again.';
                    } else if (err.code === 'auth/unauthorized-domain') {
                        errorMessage = 'Your domain is not authorized. Please check Firebase Console settings.';
                    } else if (err.code === 'auth/account-exists-with-different-credential') {
                        errorMessage = 'An account with this email already exists using different credentials. Try another sign-in method.';
                    } else if (err.code === 'auth/auth-domain-config-error') {
                        errorMessage = 'Authentication domain configuration error. Please contact support.';
                    } else if (err.code === 'auth/operation-not-allowed') {
                        errorMessage = 'Google Sign-In is not enabled for this project. Please enable it in Firebase console.';
                    } else if (err.code === 'auth/no-such-provider') {
                        errorMessage = 'Authentication not allowed in this browser. Please open in a standard browser (e.g., Chrome, Safari) or use a dedicated mobile app if applicable.';
                    }

                    setError(errorMessage);
                    setLoading(false);
                    setInitialLoading(false); // Ensure initial loading is false even on redirect error
                });
        } else {
             // If not using redirect, ensure initialLoading is false after auth state is determined
             setInitialLoading(false);
        }

        return () => unsubscribe();
    }, [validateAndSetUser, useRedirect]); // Dependencies

    const signInWithGoogle = async () => {
        setLoading(true);
        setError('');

        try {
            googleProvider.setCustomParameters({ prompt: 'select_account' });

            if (useRedirect) {
                await signInWithRedirect(auth, googleProvider);
                // Page will redirect, code below won't execute immediately.
            } else {
                await signInWithPopup(auth, googleProvider);
                // onAuthStateChanged listener will handle result.user
            }
        } catch (err) {
            console.error(`useFirebaseAuth: Authentication error in ${context}:`, err);
            let errorMessage = 'Failed to sign in with Google. Please try again.';

            if (err instanceof Error && err.message.includes('Access restricted to')) {
                errorMessage = err.message;
            } else if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
                errorMessage = 'Sign-in was cancelled. Please try again.';
            } else if (err.code === 'auth/popup-blocked') {
                errorMessage = 'Pop-up was blocked by your browser. Please allow pop-ups and try again.';
            } else if (err.code === 'auth/unauthorized-domain') {
                errorMessage = 'Your domain is not authorized for Google Sign-In. Please check Firebase Console settings.';
            } else if (err.code === 'auth/account-exists-with-different-credential') {
                errorMessage = 'An account with this email already exists using different credentials. Try another sign-in method.';
            } else if (err.code === 'auth/auth-domain-config-error') {
                errorMessage = 'Authentication domain configuration error. Please contact support.';
            } else if (err.code === 'auth/operation-not-allowed') {
                errorMessage = 'Google Sign-In is not enabled for this project. Please enable it in Firebase console.';
            } else if (err.code === 'auth/no-such-provider') {
                errorMessage = 'Authentication not allowed in this browser. Please open in a standard browser (e.g., Chrome, Safari) or use a dedicated mobile app if applicable.';
            }

            setError(errorMessage);
        } finally {
            setLoading(false); // Ensure loading is false even on error
        }
    };

    const signOut = async () => {
        setLoading(true); // Set loading while signing out
        setError('');
        try {
            await firebaseSignOut(auth);
            console.log(`useFirebaseAuth: Sign-out successful for ${context}`);
            setUser(null); // Manually clear user state on sign out
        } catch (err) {
            console.error(`useFirebaseAuth: Sign-out error in ${context}:`, err);
            setError('Failed to sign out. Please try again.');
        } finally {
            setLoading(false); // Stop loading after sign out
        }
    };

    return {
        user, // This is now the full Firebase User object or null
        loading: loading || initialLoading, // Combine loading states
        error,
        signInWithGoogle,
        signOut,
        isAuthenticated: !!user,
    };
};

export default useFirebaseAuth;